import { html, nothing, TemplateResult } from 'lit';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { createRows } from '../../utils';
import { WEB_COMPONENT_INLINE_ROW_ACTIONS } from '../../../../webcomponents.constants';
import { InlineRowActionsWebComponent } from '../../../../grid/uiElements/inlineRowActions';
import { EASMActions, FindingDetailsPayload, FindingGroup, RelatedFinding } from '../../findingDetails/types';
import { GzTooltipWebComponent } from '../../../../ui/gzTooltip';
import { BaseSpecificDetailsView } from '../../specificDetailViewByType/baseSpecificDetailsView';
import { UNAVAILABLE_DATA } from '../../../types';

export class DnsRecordsView extends BaseSpecificDetailsView {
  static getGeneralInfo(findingDetails: FindingDetailsPayload, dependencies: any): TemplateResult {
    const detailsInfo = this.getDetailsInfo(findingDetails);
    const detailsInfoEndBlock = this.getDetailsInfoEndBlock(findingDetails);
    const menuBtn = this.createMenuButton(findingDetails);

    const rows = createRows(detailsInfo, UNAVAILABLE_DATA);
    const rowsEndBlock = createRows(detailsInfoEndBlock, UNAVAILABLE_DATA);

    return html` ${rows} ${this.createEmailRow(findingDetails, menuBtn)} ${rowsEndBlock} `;
  }

  static getRelatedInfo(relatedFinding: RelatedFinding): TemplateResult | typeof nothing {
    return html`
      ${relatedFinding.status === 'SERVFAIL'
        ? html`
            <gz-text class="finding-detail" fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}">
              ${translateFn('Resolution failed')}
            </gz-text>
          `
        : nothing}
      ${relatedFinding.isVulnerable
        ? html`
            <gz-text class="finding-detail" fontWeight="${FontWeights.NORMAL}" fontSize="${FontSizes.PARAGRAPH_12}">
              ${translateFn('Vulnerable to zone transfer')}
            </gz-text>
          `
        : nothing}
    `;
  }

  private static getDetailsInfo(findingDetails: FindingDetailsPayload) {
    return [
      {
        label: translateFn('Record type'),
        value: findingDetails.recordType,
      },
    ];
  }

  private static getDetailsInfoEndBlock(findingDetails: FindingDetailsPayload) {
    return [
      {
        label: translateFn('Mail server'),
        value: findingDetails.mailServer,
      },
      {
        label: translateFn('Name server'),
        value: findingDetails.nameServer,
      },
      {
        label: translateFn('CNAME'),
        value: findingDetails.cname,
      },
      {
        label: translateFn('Vulnerable to zone transfer'),
        value: findingDetails.isVulnerable ? translateFn('Yes') : translateFn('No'),
      },
      {
        label: translateFn('DNS resolution status'),
        value: findingDetails.status,
      },
    ];
  }

  private static createMenuButton(
    findingDetails: FindingDetailsPayload
  ): InlineRowActionsWebComponent | GzTooltipWebComponent | symbol {
    const menuBtn = window.document.createElement(WEB_COMPONENT_INLINE_ROW_ACTIONS) as InlineRowActionsWebComponent;
    menuBtn.inlineActions = [
      {
        title: translateFn('Copy to clipboard'),
        value: EASMActions.COPY_TO_CLIPBOARD_EMAIL,
      },
      {
        title: translateFn('View incidents'),
        value: EASMActions.VIEW_INCIDENTS_EMAIL,
      },
    ];

    if (!findingDetails.canPivotToIncidents) {
      menuBtn.disabled = true;
    }

    if (findingDetails.email) {
      return findingDetails.canPivotToIncidents ? menuBtn : this.createMenuButtonTooltip(menuBtn);
    }

    return nothing;
  }

  private static createMenuButtonTooltip(menuBtn: InlineRowActionsWebComponent): GzTooltipWebComponent {
    const menuBtnTooltip = new GzTooltipWebComponent();
    menuBtnTooltip.tooltipText = translateFn('EDR license required');
    menuBtnTooltip.appendChild(menuBtn);
    return menuBtnTooltip;
  }

  private static createEmailRow(
    findingDetails: FindingDetailsPayload,
    menuBtn: InlineRowActionsWebComponent | GzTooltipWebComponent | symbol
  ): TemplateResult {
    return html`
      <gz-labeled-content label="${translateFn('Email')}" class="labeled-row" labelFontSize="14">
        <div class="d-flex align-items-center item-with-action">
          <gz-tooltip tooltipText="${findingDetails.email ?? UNAVAILABLE_DATA}" isEllipsis>${findingDetails.email ?? UNAVAILABLE_DATA}</gz-tooltip>
          ${menuBtn}
        </div>
      </gz-labeled-content>
    `;
  }

  static getSubtitle(relatedFindings: FindingGroup): string {
    let resolutionFailed = 0;
    let vulnerableToZoneTransfer = 0;
    for (const finding of relatedFindings.items) {
      if (finding.status === 'SERVFAIL') {
        resolutionFailed++;
      }
      if (finding.isVulnerable) {
        vulnerableToZoneTransfer++;
      }
    }

    return translateFn(
      '{resolutionFailed}/{total} resolution failed, {vulnerableToZoneTransfer}/{total} vulnerable to zone transfer',
      {
        resolutionFailed: resolutionFailed,
        vulnerableToZoneTransfer: vulnerableToZoneTransfer,
        total: relatedFindings.items.length,
      }
    );
  }
}
